// ---- Hunting (client, plan 11) ------------------------------------------------
// The fifth focus.ts-sibling activity, beside combat.ts (fight), talk.ts
// (converse), interact.ts (props), and fishing.ts (fish). Two loops:
//
//   STALKING (active): with a hunting bow equipped (a ranged weapon carrying
//   the `hunting` marker) and a prey focused, HOLD RMB to draw — game.aiming
//   locks movement and the camera eases over the shoulder (camera.ts
//   updateAimCamera, driven from game.ts like the talk cutscene). RELEASE
//   looses the arrow through the EXISTING ranged fire path (combat.ts
//   playerFire — injected by game.ts to keep the import graph acyclic); the
//   server folds the aim bonus, rolls the band, and routes a prey kill to
//   Hunting/stalking XP at the kill site. Esc cancels the draw cleanly. The
//   approach itself is the flee system — run and prey bolts before you're in
//   range; that's the mechanic, not code here.
//
//   TRAPPING (passive): with a snare in the bag, on dry ground, the Set Trap
//   slot anchors it (`deployTrap`); the server owns the durable soak timer and
//   everyone sees the snare (the `traps`/`trap` mirror below — the fishing-nets
//   pattern on land). Return once ready and the same slot reads Collect. No
//   camera change, no input lock — relaxed.
//
// Wiring rule (game.ts): initHunting runs AFTER initFishing and BEFORE
// initFocus — its Esc must cancel the draw before focus.ts would drop the
// focus, and its canvas pointerdown guards clicks while drawn.

import * as THREE from "three";
import { game, inputLocked } from "./state.ts";
import { getFocus } from "./focus.ts";
import { heightAt, WATER_LEVEL } from "./terrain.ts";
import { itemSpec, ITEMS } from "./items/index.ts";
import { isTrap, bandFor, buildItem, type ItemSpec } from "./items/define.ts";
import { equippedType, countOf, flashToast } from "./inventory.ts";
import { sendDeployTrap, sendCollectTrap } from "./net.ts";
import { hasBoxUnlocked } from "./skill.ts";
import { SKILLS, huntingSum } from "./skill/index.ts";
import { resetAimCamera } from "./camera.ts";
import { stalkHitChance } from "./sim/hunting.ts";
import { WATER_MARGIN } from "./sim/fishing.ts";
import { HUNTING_TUNING as HT } from "./tunables.ts";
import type { Live } from "./npc/index.ts";
import type { ItemType, TrapDeploy } from "./shared.ts";

const AIM_EXIT_DUR = 0.5; // camera ease-back window (mirrors talkExit/fishingExit)

let scene: THREE.Scene;
let fire: () => void = () => {}; // combat.ts playerPunch, injected by game.ts

let promptEl: HTMLElement | null = null;
let promptShown = "";

// ---- Deployed-trap mirror (server-owned; everyone sees the snare) -------------
const trapsMap = new Map<string, TrapDeploy>();
const trapGroups = new Map<string, THREE.Group>();

// The snare model: the first trap item's own build (CP1 has one trap type; the
// wire doesn't carry the item type, same as net floats being generic).
let trapModel: ItemSpec | null | undefined;
function buildTrapGroup(): THREE.Group {
  trapModel ??= ITEMS.find(isTrap) ?? null;
  return trapModel ? buildItem(trapModel) : new THREE.Group();
}

function addTrapGroup(tr: TrapDeploy): void {
  const g = buildTrapGroup();
  g.position.set(tr.x, heightAt(tr.x, tr.z), tr.z);
  scene.add(g);
  trapGroups.set(tr.id, g);
}

function removeTrapGroup(id: string): void {
  const g = trapGroups.get(id);
  if (g) scene.remove(g);
  trapGroups.delete(id);
}

/** Full sync (join/reconnect): replace the whole mirror. */
export function setTraps(list: TrapDeploy[]): void {
  for (const id of [...trapGroups.keys()]) removeTrapGroup(id);
  trapsMap.clear();
  for (const tr of list) {
    trapsMap.set(tr.id, tr);
    addTrapGroup(tr);
  }
}

/** One trap deployed/collected (broadcast, personalized `mine`). */
export function applyTrapDelta(op: "add" | "remove", trap?: TrapDeploy, id?: string): void {
  if (op === "add" && trap) {
    trapsMap.set(trap.id, trap);
    removeTrapGroup(trap.id); // belt-and-braces against a replayed add
    addTrapGroup(trap);
  } else if (op === "remove" && id) {
    trapsMap.delete(id);
    removeTrapGroup(id);
  }
}

// ---- Shared little helpers ------------------------------------------------------

/** Some other activity owns the input (the shared lock; game.aiming is checked
 *  separately by the callers to tell "start a draw" from "already drawn"). */
function busy(): boolean {
  return inputLocked();
}

/** The equipped HUNTING BOW's item spec, or null (equipped weapon without the
 *  `hunting` marker — a war bow — doesn't count; that's the tool-gate). */
export function equippedHuntingBow(): ItemSpec | null {
  const t = equippedType();
  if (!t) return null;
  const spec = itemSpec(t);
  return spec.hunting && spec.weapon?.ranged ? spec : null;
}

/** The folded aim bonus: the bow's marker + the mirrored unlocked boxes. Pure
 *  UX (the prompt's hit%) — the server re-derives its own at fire time.
 *  Folded ONCE per draw (startAim) and stashed — it can't change mid-draw, and
 *  updateHunting reads it every frame while drawn. */
function aimBonus(bow: ItemSpec): number {
  return (bow.hunting?.aimHitBonus ?? 0) + huntingSum(SKILLS, hasBoxUnlocked).aimHitBonus;
}
let drawnAimBonus = 0; // the fold captured at draw start

/** Dry, huntable ground (the deploy validity — the server checks the same). */
function isDry(x: number, z: number): boolean {
  return heightAt(x, z) >= WATER_LEVEL - WATER_MARGIN;
}

/** First dry point straight ahead within reach (where the snare drops). The
 *  slot ACTION uses this fresh; the per-frame slot state goes through the
 *  cached probe below. */
function forwardDryPoint(max: number): { x: number; z: number } | null {
  const p = game.player.position;
  const ry = game.player.rotation.y;
  const dx = Math.sin(ry), dz = Math.cos(ry);
  for (let d = 1.5; d <= max; d += 1) {
    const x = p.x + dx * d, z = p.z + dz * d;
    if (isDry(x, z)) return { x, z };
  }
  return null;
}

// The probe costs heightAt() samples (4-octave noise) and trapSlotState asks
// EVERY frame while a trap is carried — cache with a short TTL, invalidated by
// moving or turning (the fishing.ts cachedForwardWater discipline).
const dryProbe = { at: 0, x: 0, z: 0, ry: 0, result: null as { x: number; z: number } | null };
function cachedForwardDry(max: number): { x: number; z: number } | null {
  const now = performance.now();
  const p = game.player.position, ry = game.player.rotation.y;
  if (now - dryProbe.at < 200 && Math.abs(ry - dryProbe.ry) < 0.1 && Math.hypot(p.x - dryProbe.x, p.z - dryProbe.z) < 0.5) {
    return dryProbe.result;
  }
  Object.assign(dryProbe, { at: now, x: p.x, z: p.z, ry, result: forwardDryPoint(max) });
  return dryProbe.result;
}

// The deployable trap types, resolved once (content is static).
let trapTypes: ItemType[] | null = null;

/** Any deployable trap we're carrying (first match — CP1 has one trap type). */
function carriedTrapType(): ItemType | null {
  trapTypes ??= ITEMS.filter(isTrap).map((s) => s.type);
  for (const type of trapTypes) {
    if (countOf(type) > 0) return type;
  }
  return null;
}

/** The nearest of OUR deployed traps within reach, or null. */
function nearestOwnTrap(): TrapDeploy | null {
  const p = game.player.position;
  let best: TrapDeploy | null = null, bd = Infinity;
  for (const tr of trapsMap.values()) {
    if (!tr.mine) continue;
    const d = Math.hypot(p.x - tr.x, p.z - tr.z);
    if (d <= HT.trapRange && d < bd) { bd = d; best = tr; }
  }
  return best;
}

// ---- The stalking draw ------------------------------------------------------------

/** The focused prey while drawn: a living fightable focus (the fire path is
 *  focus-locked, so the draw is too). */
function drawTarget(): Live | null {
  const f = getFocus() as Live | null;
  return f && f.fightable && !f.defeated ? f : null;
}

function startAim(): void {
  if (game.aiming || busy()) return;
  const bow = equippedHuntingBow();
  if (!bow) return; // no marker → no aim cam (a war bow stays plain combat)
  if (!drawTarget()) { flashToast("Focus your quarry first"); return; }
  game.aiming = true;
  drawnAimBonus = aimBonus(bow); // invariant for the whole draw
  resetAimCamera(); // ease in from the live view (game.ts drives the camera)
}

/** Drop the draw. `loose` fires through the injected combat path first. */
function endAim(loose: boolean): void {
  if (!game.aiming) return;
  game.aiming = false;
  game.aimingExit = AIM_EXIT_DUR; // ease the camera back (game.ts)
  showPrompt("");
  if (loose) fire(); // combat.ts playerFire: gates + ammo + the `fire` intent
}

/** Map swap (plan 24, game.ts swapWorld): drop the draw without loosing, and
 *  cut the exit ease (the camera must not ease back across two worlds). */
export function resetAiming(): void {
  endAim(false);
  game.aimingExit = 0;
}

/** Where the aim camera looks — the focused prey's mid-height, or null once
 *  the draw has nothing to sight on (game.ts falls back to the exit ease). */
export function getAimTarget(): { x: number; y: number; z: number } | null {
  const L = drawTarget();
  if (!L) return null;
  const p = L.group.position;
  return { x: p.x, y: p.y + (L.spec.height ?? 1.2) * 0.55, z: p.z };
}

// ---- Action-bar slot state (combat.ts owns the bar and renders these) ------------

/** The Set Trap / Collect slot: collect wins when one of our traps is in reach. */
export function trapSlotState(): { show: boolean; enabled: boolean; label: string; icon: string } {
  if (game.aiming || busy()) { // busy() already covers fishing
    return { show: false, enabled: false, label: "", icon: "" };
  }
  const own = nearestOwnTrap();
  if (own) {
    const left = own.readyAt - Date.now();
    if (left <= 0) return { show: true, enabled: true, label: "Collect", icon: "🪤" };
    return { show: true, enabled: false, label: `Soaking ${Math.ceil(left / 60_000)}m`, icon: "🪤" };
  }
  if (carriedTrapType() && cachedForwardDry(3)) {
    return { show: true, enabled: true, label: "Set Trap", icon: "🪤" };
  }
  return { show: false, enabled: false, label: "", icon: "" };
}

/** Trap slot click (Set Trap / Collect). */
function trapSlotAction(): void {
  if (game.aiming || busy()) return;
  const own = nearestOwnTrap();
  if (own) {
    if (Date.now() >= own.readyAt) sendCollectTrap(own.id);
    return;
  }
  const type = carriedTrapType();
  const spot = type ? forwardDryPoint(3) : null;
  if (!type || !spot) return;
  sendDeployTrap(type, spot.x, spot.z);
}

// ---- Init + per-frame -------------------------------------------------------------

function showPrompt(text: string): void {
  if (!promptEl || promptShown === text) return;
  promptShown = text;
  promptEl.textContent = text;
  promptEl.style.display = text ? "block" : "none";
}

/** Wire up hunting. MUST run after initFishing and before initFocus (see the
 *  header): our Esc cancels the draw ahead of focus.ts, and our canvas
 *  pointerdown swallows clicks while drawn. `fireAction` = combat.ts
 *  playerPunch (injected — combat.ts already imports our slot state, so a
 *  direct import here would cycle). */
export function initHunting(s: THREE.Scene, canvas: HTMLCanvasElement, fireAction: () => void): void {
  scene = s;
  fire = fireAction;
  promptEl = document.getElementById("huntprompt");

  document.getElementById("trapBtn")?.addEventListener("click", trapSlotAction);

  // The draw: hold RMB, release to loose. Window-level so a drag off the canvas
  // still resolves (the block hold uses the same discipline; with a two-hand bow
  // equipped no shield can be raised, so the block no-ops and RMB is ours).
  window.addEventListener("pointerdown", (e) => {
    if (e.button !== 2 || !game.joined) return;
    startAim();
  });
  window.addEventListener("pointerup", (e) => {
    if (e.button !== 2 || !game.aiming) return;
    endAim(true); // release = loose (playerFire re-checks range/ammo/cooldown)
  });
  window.addEventListener("blur", () => endAim(false));

  // While drawn, the canvas click is ours — never re-target focus / pick items
  // mid-draw (registered before initFocus, so stopImmediatePropagation works).
  canvas.addEventListener("pointerdown", (e) => {
    if (game.aiming && e.button === 0) e.stopImmediatePropagation();
  });

  addEventListener("keydown", (e) => {
    if (!game.aiming) return;
    if (e.key === "Escape") {
      endAim(false); // cancel without loosing
      flashToast("Draw cancelled");
      e.stopImmediatePropagation(); // focus.ts never sees it — the focus holds
    }
  });
}

/** Per-frame: keep the draw honest (target/bow/KO can all vanish under us) and
 *  feed the aim prompt its live hit%. */
export function updateHunting(_dt: number, _t: number): void {
  if (!game.aiming) return;
  const bow = equippedHuntingBow();
  const L = drawTarget();
  if (!bow || !L || game.down) { endAim(false); return; }
  const p = game.player.position, q = L.group.position;
  const d = Math.hypot(p.x - q.x, p.z - q.z);
  const bands = bow.weapon!.ranged!.bands;
  const cap = bow.weapon!.attackRange;
  if (d > cap) {
    showPrompt(`Too far — ${d.toFixed(0)}u (reach ${cap}u)`);
    return;
  }
  const pct = Math.round(stalkHitChance(bandFor(bands, d).hit, drawnAimBonus) * 100);
  showPrompt(`Drawn on the ${L.spec.name} — ${pct}% at ${d.toFixed(1)}u · release to loose`);
}
